import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Loading from "../Loading";
import PrintButton from "./elements/PrintWine";
import WineDetails from "./elements/WineDetails";
import WineSearcher from "./elements/WineSearcher";
import WineVintages from "./elements/WineVintages";
import { formatTitle } from '../../../utils/helper';
import WinePriceHistory from "./elements/WinePriceHistory";
import Head from "next/head";
import Link from 'next/link';
import WineReviewList from "./elements/WineReviewList";
import { useToast } from "../../../context/toastContext";
import { useAuth } from "../../../context/authContext";
import { Wine } from "../../../context/wineContext/reducer";
import Image from "next/image";
import ShowOnBanquetImage from "../../../public/img/find_on_banquet.svg";
import { nameProducerAndVintage } from "../../../utils/wine";
import WineImportersList from "./elements/WineImportersList";
import { usePrintWebPage } from "../../../hooks/usePrintWebPage";
import Button from "../Buttons/Button";
import { generateWineDescription } from "../../../utils/metaTags";

interface WineDetailProps {
  baseWine?: any;
  vintage?: string | null;
  error?: string;
}

const WineDetail = ({baseWine, vintage, error}: WineDetailProps) => {
  const router = useRouter();
  const toast = useToast();
  const { slug, vintage: vintageRaw } = router.query;
  const [activeWine, setActiveWine] = useState<Wine>();
  const [lwin11, setLwin11] = useState(null);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const {
    state: { user },
  } = useAuth();
  const printRef = React.useRef(null);
  const { handlePrint } = usePrintWebPage({
    printRef: printRef,
    pageStyle: `margin: 10px;`,
    documentTitle: baseWine?.wine_name,
  });

  useEffect(() => {
    initializeActiveWine();
  }, []);

  useEffect(() => {
    if (vintage && baseWine?.lwin7) {
      setLwin11(baseWine.lwin7+vintage);
    }
  }, [vintage, baseWine]);

  const description = useMemo(() => {
    return generateWineDescription(
      +vintage || activeWine?.vintage || null,
      baseWine?.producer?.producer_name || "",
      baseWine?.wine_name || "",
      baseWine?.grape_variety || null,
      activeWine ? activeWine.region_2 : baseWine?.wines?.[0]?.wine?.region_2 || null,
      activeWine ? activeWine.author : baseWine?.wines?.[0]?.wine?.author || null
    );
  }, [activeWine, baseWine]);

  const initializeActiveWine = async () => {
    try {
      if (error) {
        toast("error", error);
        return;
      }
      if (!vintage) return;

      if (router.query.wineId || baseWine?.slug !== slug) {
        router.replace(`/wines/${baseWine.slug}/${vintage}`, undefined, {
          shallow: true,
          scroll: false,
        });
      }

      const baseWineVintage = vintage ? vintage : baseWine.wine_vintages[0].vintage;

      if (!!baseWine?.wines) {
        const wine = baseWine.wines.find((w) => {
          return w.wine.vintage.toString().toLowerCase() === baseWineVintage.toString().toLowerCase()
        });
        setActiveWine(wine.wine);
      }
    } catch (error) {
      toast("error", `Unable to load Wine Reviews for vintage ${vintage}`);
    }
  }

  const { newestVintageRegion1, newestVintageRegion2 } = useMemo(() => {
  const newestWine = baseWine?.wines?.reduce((latest, w) => {
    return (!latest || w.wine.vintage > latest?.vintage) ? w.wine : latest;
  }, null);

  return {
    newestVintageRegion1: newestWine?.region_1,
    newestVintageRegion2: newestWine?.region_2,
  };
}, [baseWine?.wines]);

  const wineSearcherUrl = "http://www.wine-searcher.com/find/" + slug + "/" + vintage
  const shopbanquetUrl = useMemo(() => {
    const baseUrl = "https://shopbanquet.com/search?f=q:";
    let query = ""
    if (activeWine) {
      query = nameProducerAndVintage(activeWine);
    } else if (baseWine && vintage) {
      query = `${vintage} ${baseWine.wine_wine}`
    } else if (baseWine) {
      query = baseWine.wine_wine;
    }

    return baseUrl + encodeURIComponent(query);
  }, [activeWine, baseWine, vintage]);

  const displayableImporters = useMemo(() => {
    return baseWine && vintage ? baseWine.importers?.filter(importer => importer.display_imports && !importer.hide_for_producers) : [];
  }, [activeWine, baseWine, vintage]);

  function addWineJsonLd() {
    if (baseWine?.wine_name) {
      try {
        let reviews = [];
        let aggregateRating;
        const winesSource = Array.isArray(baseWine?.wines) ? baseWine.wines : [];

        // If vintage we filter out unrelated wine vintages
        const wines = vintage
          ? winesSource.filter((wine) => wine?.wine?.vintage == vintage)
          : winesSource;
        // Iterate through wines to generate individual reviews
        reviews = 
          wines
            .map((wine) => {
              if (
                !wine?.wine?.author ||
                !(wine?.wine?.score && Number(wine.wine.score) > 0)
              )
                return null;
              const review = {
                "@type": "Review",
                author: {
                  "@type": "Person",
                  name: `${wine.wine.author}`,
                },
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: `${Math.round(Number(wine.wine.score) * 2) / 2}`,
                  bestRating: "100",
                },
              };
              return review;
            })
            .filter((review) => review !== null)

        // Generate aggregate rating by averaging out wines that have scores
        const ratedWines = wines.filter((wine) => Number(wine?.wine?.score) > 0);
        const scoresCount = ratedWines.length;

        if (scoresCount > 0) {
          const aggregateRatingScore =
            Math.round(
              (ratedWines.reduce((prev, wine) => {
                return prev + Number(wine.wine.score);
              }, 0) /
                scoresCount) *
                2
            ) / 2;

          aggregateRating = {
            "@type": "AggregateRating",
            ratingValue: `${aggregateRatingScore}`,
            bestRating: "100",
            ratingCount: scoresCount,
          };
        }
        
        const type = reviews.length > 0 || scoresCount > 0 ? "Product" : "WebPage";
        const jsonLd = {
          "@context": "https://schema.org/",
          "@type": type,
          brand: {
            "@type": "Organization",
            name: baseWine.producer?.producer_name || "",
          },
          description,
          name: activeWine ? activeWine.wine_name : baseWine?.wine_name,
          ...(reviews.length > 0 && {
            review: reviews,
          }),
          hasPart: [
            {
              "@type": "WebPageElement",
              cssSelector: ".wine-detail",
              isAccessibleForFree: true,
            },
            {
              "@type": "WebPageElement",
              cssSelector: ".wine-review-paywall",
              isAccessibleForFree: false,
            },
            {
              "@type": "WebPageElement",
              cssSelector: ".wine-score-paywall",
              isAccessibleForFree: false,
            },
          ],
          ...(type === "Product" && aggregateRating && { aggregateRating })
        };

        return JSON.stringify(jsonLd, null, 2);
      } catch (e) {
        // Return bare minimum info on error
        return JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "WebPage",
          name: baseWine?.wine_name || "",
          description,
          brand: {
            "@type": "Organization",
            name: baseWine?.producer?.producer_name || "",
          },
          hasPart: [
            {
              "@type": "WebPageElement",
              cssSelector: ".wine-detail",
              isAccessibleForFree: true,
            },
            {
              "@type": "WebPageElement",
              cssSelector: ".wine-review-paywall",
              isAccessibleForFree: false,
            },
            {
              "@type": "WebPageElement",
              cssSelector: ".wine-score-paywall",
              isAccessibleForFree: false,
            },
          ],
        }, null, 2);
      }
    }
  }

  let vintageLabel = "";
  if(activeWine && activeWine?.vintage !== 0) vintageLabel = activeWine.vintage.toString();
  if(vintage && vintage !== "0") vintageLabel = vintage;
  if(vintage && vintage === "0") vintageLabel = "NV";

  // vintageLabel is a display label, not a URL segment. "NV" and 0 have no
  // route - /wines/:slug/NV and /wines/:slug/0 both return 200 but render the
  // empty shell (a soft 404), so only a real four-digit year may appear in the
  // canonical. Everything else canonicalises to /wines/:slug.
  const canonicalVintage = /^\d{4}$/.test(String(vintageLabel)) ? vintageLabel : "";

  const handleSelectedReviews = useCallback((wineId: number) => {
    if (selectedReviews.includes(wineId)) {
      setSelectedReviews(selectedReviews.filter(item => item !== wineId));
    } else {
      setSelectedReviews([...selectedReviews, wineId]);
    }
  }, [selectedReviews]);

  return (
    <div>
      {baseWine?.wine_name &&
        <Head>
          <title>{formatTitle(`${baseWine.producer?.producer_name ?? ""} ${baseWine.wine_name} ${vintageLabel}`)}</title>
          <meta name="description" content={description}/>
          <meta name="viewport" content="width=device-width, initial-scale=1"/>
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={`https://vinous.com/wines/${baseWine.slug}${canonicalVintage ? `/${canonicalVintage}` : ""}`}/>
          <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: addWineJsonLd()}}
              key="wine-jsonld"
          />
        </Head>
      }
      <div ref={printRef}>
        {
          baseWine?.wine_name &&
          <h1 className="text-3xl font-bold pt-4 pb-8 text-center">
            { vintageLabel } { baseWine.wine_name }
          </h1>
        }
        {
          baseWine?.wines &&
          <div className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-8 xl:space-x-12 2xl:space-x-16">
            <div className="w-full lg:w-1/4 space-y-8 wine-detail">
              <div>
                <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                  Wine Details
                </div>

                <WineDetails
                  baseWine={baseWine}
                  wineRegion2={activeWine?.region_2 || newestVintageRegion2}
                  wineRegion1={activeWine?.region_1 || newestVintageRegion1 }
                />
              </div>

              {
                baseWine.wine_vintages &&
                <div className="no-print">
                  <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                    Vintages
                  </div>

                  <WineVintages
                    key={vintage}
                    className="lg:grid-cols-4"
                    vintage={vintage}
                    vintages={baseWine.wine_vintages}
                    hrefBase={`/wines/${slug}`}
                  />
                </div>
              }

              <div className="hidden lg:block no-print">
                <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                </div>

                <PrintButton label="Print Wines" onClick={handlePrint} />
                <Button
                  label="Make Shelftalkers"
                  onClick={() => {
                    if (selectedReviews.length === 0) {
                      toast("error", "Please select at least one review to create shelftalkers.");
                      return;
                    }
                     
                    router.push(`/wines/${baseWine.slug}/${vintage}/shelftalkers?selectedReviews=${selectedReviews}`)
                  }}
                  type="tertiary"
                  className="mt-1"
                ></Button>
              </div>
            </div>
            <div className="w-full lg:w-3/4">
              <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                Reviews & Tasting Notes
              </div>

              <div className="space-y-2">
                <WineReviewList
                  wines={[...baseWine.wines]}
                  activeWine={activeWine}
                  vintage={vintageRaw}
                  handleSelectedReviews={handleSelectedReviews}
                />
              </div>

              {displayableImporters.length !== 0 && (
                <>
                  <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4 mt-12">
                    Importer Details
                  </div>
                  <WineImportersList importers={displayableImporters}/>
                </>
              )}

              { // Load livex price data for authenticated users only
                vintage && activeWine && lwin11 && user && (
                  <div className="border-b border-gray-100 pb-2 mb-4 mt-12">
                    <h2 className="font-light text-xl w-full">Price History</h2>
                    <span className="font-light w-full">Historic Pricing Data from Liv-ex</span>
                      <WinePriceHistory
                        wineName={activeWine.wine_name}
                        lwin11={lwin11}
                      />
                  </div>
                )
              }

              { // Load prices & retailers data for authenticated users only
                activeWine?.id && user && (
                  <>
                    <div className="border-b border-gray-100 pb-2 mb-4 mt-12 flex items-center no-print">
                      <h3 className="font-light text-xl w-full">Prices & Retailers</h3>
                      <Link href={shopbanquetUrl} target="_blank" rel="noopener noreferrer">
                        <Image className="h-14" alt="Find on Banquet" src={ShowOnBanquetImage} />
                      </Link>
                    </div>

                    <div>
                      {activeWine?.id && <WineSearcher
                        wine_id={activeWine.id}
                      />}
                    </div>

                    <Link href={wineSearcherUrl} passHref target="_blank" rel="noopener noreferrer" className="font-light pb-8 pt-3 flex items-center justify-center no-print">
                      <Image width="200" height="50" alt="WineSearcher" src="/img/powered-by-wine-searcher.png" />
                    </Link>

                    <div className="block lg:hidden mt-12 no-print">
                      <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                      </div>

                      <PrintButton label="Print Wines" onClick={handlePrint}/>
                    </div>
                  </>
                )
              }
            </div>
          </div>
        }
      </div>
    </div>
  )
}

export default WineDetail;
